import { Address } from './common';
import { Timestamp, FieldValue } from 'firebase/firestore';

export type CustomerType = 'individual' | 'organization';

// Define a more flexible Customer type that can accept FieldValue in timestamps
export type CustomerWithFieldValues = Omit<Customer, 'createdAt' | 'updatedAt' | 'importedAt'> & {
  createdAt?: Date | Timestamp | FieldValue | string;
  updatedAt?: Date | Timestamp | FieldValue | string;
  importedAt?: Date | Timestamp | FieldValue | string;
};

export interface ContactPerson {
  name: string;
  email: string;
  phone: string;
  position: string;
}

export interface InvoiceGroup {
  id: string;
  name: string;
  podIds: string[];
  createdAt: Date | Timestamp;
  updatedAt?: Date | Timestamp;
}

export type BalanceGroupType = 'MVM' | 'EON';

export interface BalanceGroupInfo {
  type: BalanceGroupType;
  code: string;
  fullName: string;
}

export const BALANCE_GROUPS: Record<BalanceGroupType, BalanceGroupInfo> = {
  MVM: {
    type: 'MVM',
    code: '15X-DENERGIA---J',
    fullName: 'MVM Next Energiakereskedelmi Zrt.'
  },
  EON: {
    type: 'EON',
    code: '39XEON-ENMEGOLDC',
    fullName: 'EON Energiamegoldások Kft.'
  }
};

export interface BulkInvoicingSettings {
  invoicingMode: 'per_pod' | 'consolidated' | 'all_pods_single_invoice';
  defaultInvoiceGroupId?: string;
  indexedPricing?: boolean; // Indexált árazást használ flag
}

// Interface for tracking customer data change history
export interface CustomerChangeHistory {
  id: string;
  customerId: string;
  timestamp: Timestamp | Date;
  userId: string;
  userName?: string;
  changedFields: {
    [key: string]: {
      oldValue: any;
      newValue: any;
    }
  };
  changeType: 'create' | 'update' | 'delete';
  snapshot?: Partial<CustomerWithFieldValues> | undefined; // Optional full snapshot of customer data at time of change
}

// Interface for customer relationship log entries
export interface CustomerRelationshipLog {
  id: string;
  customerId: string;
  entryId: string; // Format: {taxNumber_first8chars}_{YYYYMMDD}_{sequenceNumber}
  content: string; // Max 1000 characters
  contactType: 'phone' | 'email' | 'distributor' | 'internal' | 'postal'; // Kapcsolattartás típusa
  createdAt: Timestamp | Date;
  createdBy: string;
  createdByName?: string;
  isOpen: boolean; // Whether there are pending tasks for this log entry
}

export interface CreateCustomerData {
  type: CustomerType;
  customerCode?: string; // 4 jegyű egyedi ügyfélazonosító (0001, 0002, stb.) - az ajánlat/szerződés számozáshoz
  companyName?: string;
  firstName?: string;
  lastName?: string;
  email: string;
  phone?: string;
  taxNumber?: string;
  euVatNumber?: string;
  address: Address;
  contactPerson: ContactPerson;
  organizations: string[];
  notes?: string;
  documents: Array<{
    id: string;
    name: string;
    originalName?: string;
    url: string;
    uploadedAt: Timestamp | Date;
    type?: string;
    size?: number;
  }>;
  bulkInvoicingSettings?: BulkInvoicingSettings;
  invoiceGroups?: InvoiceGroup[];
  balanceGroup?: BalanceGroupType;
}

export interface Customer extends CustomerFormData {
  id: string;
  customerCode?: string; // 4 jegyű egyedi ügyfélazonosító (0001, 0002, stb.) - az ajánlat/szerződés számozáshoz
  createdAt: Date | Timestamp;
  updatedAt?: Date | Timestamp | string;  // Firebase-ben ez lehet string is
  createdBy?: string;  // Opcionális, mert lehet nincs minden ügyfélen
  status: 'lead' | 'prospect' | 'quoted' | 'contract' | 'active' | 'inactive' | 'lost';
  relationshipLogs?: CustomerRelationshipLog[];
  consultant?: {
    id: string;
    companyName: string;
  };
}

export interface CustomerFormData {
  type: CustomerType;
  customerCode?: string; // 4 jegyű egyedi ügyfélazonosító (0001, 0002, stb.) - az ajánlat/szerződés számozáshoz
  companyName: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  taxNumber: string;
  euVatNumber?: string;
  registrationNumber: string;
  organizations: string[];
  notes: string;
  documents: Array<{
    id: string;
    name: string;
    originalName?: string;
    url: string;
    uploadedAt: Timestamp | Date;
    type?: string;
    size?: number;
  }>;
  address: {
    street: string;
    city: string;
    postalCode: string;
    country: string;
  };
  contactPerson: {
    name: string;
    email: string;
    phone: string;
    position: string;
    fax?: string;
  };
  contractNumber?: string;
  companyAddress?: string;
  bankAccount?: string;
  representativeName?: string;
  representativePhone?: string;
  representativeEmail?: string;
  billingEmail?: string;
  additionalBillingEmails?: string[];
  financialContact?: {
    name: string;
    email: string;
    phone: string;
  };
  consumptionData?: {
    date: string;
    dueDate: string;
    power: string;
    profileNumber: string;
    consumption: string;
    monthlyConsumption?: string;  // Havi fogyasztás
    annualConsumption?: string;   // Éves fogyasztás
  };
  contractPeriod?: {
    startDate: string;
    endDate: string;
    price: string;
    priceEUR?: string; // EUR/MWh ár indexált árazáshoz
    indexedPrice?: boolean; // Indexált ár checkbox
    spread?: string; // Spread összeg (Ft/kWh) - csak indexált árnál
    priceUnit?: 'HUF/kWh' | 'EUR/MWh'; // Az ár pénzneme/egysége
    paymentCurrency?: 'HUF' | 'EUR'; // Fizetési pénznem
    currency?: 'HUF' | 'EUR'; // Fizetési pénznem (alias)
  };
  offerPrice?: {
    currency: string;
    price: string;
  };
  kat?: {
    text: string;
    kat: string;
  };
  pod?: {
    id: string;
    address: string;
    partnerId?: string;
    postalCode?: string;
    city?: string;
    street?: string;
    measurementType: string;
    meteringType?: string; // Mérés típusa (AMR, SLP, stb.)
    annualConsumption: string;
    invoiceGroupId?: string;
    multiplier?: number;
    billable?: boolean;
    billingSuspendedFrom?: string;
    billingSuspendedTo?: string;
    suspensionReason?: string;
    meterSerialNumber?: string;
    meterType?: string;
    meterInstallationDate?: string;
    meterRemovalDate?: string;
    isSlpConsumer?: boolean;
    slpAnnualConsumption?: string;
    slpMonthlyAverage?: string;
    // HMKE / KE (kiserőmű) adatok
    smallPowerPlant?: {
      type: 'none' | 'hmke' | 'ke';  // HMKE = háztartási méretű kiserőmű, KE = kiserőmű
      isReversePower: boolean;        // Visszwattos rendszer (betáplálási telj. < inverter telj.)
      installationDate?: string;      // Telepítés dátuma (elszámolás típusát befolyásolja)
      inverterCapacityKva?: string;   // Inverter névleges teljesítőképesség (kVA)
      feedInCapacityKva?: string;     // Engedélyezett betáplálási teljesítmény (kVA)
      settlementType?: 'annual_balance' | 'gross' | 'pending';  // Éves szaldó / Bruttó / Még nem eldöntött
    };
  };
  additionalPods?: Array<{
    id: string;
    address: string;
    partnerId?: string;
    postalCode?: string;
    city?: string;
    street?: string;
    measurementType: string;
    meteringType?: string; // Mérés típusa (AMR, SLP, stb.)
    annualConsumption: string;
    invoiceGroupId?: string;
    multiplier?: number;
    billable?: boolean;
    billingSuspendedFrom?: string;
    billingSuspendedTo?: string;
    suspensionReason?: string;
    meterSerialNumber?: string;
    meterType?: string;
    meterInstallationDate?: string;
    meterRemovalDate?: string;
    isSlpConsumer?: boolean;
    slpAnnualConsumption?: string;
    slpMonthlyAverage?: string;
    // HMKE / KE (kiserőmű) adatok
    smallPowerPlant?: {
      type: 'none' | 'hmke' | 'ke';
      isReversePower: boolean;
      installationDate?: string;
      inverterCapacityKva?: string;
      feedInCapacityKva?: string;
      settlementType?: 'annual_balance' | 'gross' | 'pending';
    };
  }>;
  
  balanceGroup?: BalanceGroupType;
  
  invoiceGroups?: InvoiceGroup[];
  bulkInvoicingSettings?: BulkInvoicingSettings;
  
  // EKR (Energiahatékonysági díj) beállítás
  ekrSetting?: {
    type: 'standard' | 'exempt' | 'custom';  // standard = aktuális díj, exempt = kötelezett (0 Ft), custom = egyedi ár
    customPrice?: string;  // Egyedi ár HUF/kWh (csak 'custom' típusnál)
  };

  // CSV importból származó új mezők
  paymentMethod?: string;  // Fizetési mód (15nap, 30nap, stb.)
  
  // Adatforrás információk
  dataSource?: 'manual' | 'csv_import' | 'csv_import_2026' | 'api_import';  // Adatok forrása
  importedAt?: Date | Timestamp | string;  // Importálás dátuma - lehet string is Firebase-ből
  needsReview?: boolean;  // Szükséges-e felülvizsgálat (pl. email címek)
  
  csvSourceData?: {
    szlaId: string;        // Számla ID
    ugyfelId: string;      // Ügyfél ID  
    podId: string;         // POD ID
    megj: string;          // Megjegyzés
    fixTozsde: string;     // Fix/tőzsde ár típus
    hufEur: string;        // HUF/EUR váltás
  };
  
  bankingInfo?: {
    otpHuf: string;        // OTP HUF bank
    khHuf: string;         // K&H HUF bank
    unicreditHuf: string;  // UniCredit HUF bank
    skEuro: string;        // SK Euro bank
    unicreditEuro: string; // UniCredit Euro bank
    solarHmke: string;     // Solar HMKE
  };
  
  prepayment?: {
    amount: string;        // Előleg összeg
    quantityKwh: string;   // Előleg mennyiség kWh-ban
  };
  
  // Új strukturált árazási rendszer
  currentPricing?: PricingData;
  futurePricing?: PricingData[];
  pricingStructure?: PricingStructure;
  hasFuturePricing?: boolean;

  // Földgáz modul kapcsolódások
  isGasCustomer?: boolean;
  gasPodIds?: string[];
}

interface FormField {
  name: string;
  label: string;
  required: boolean;
  type?: string;
  options?: Array<{ value: string; label: string }>;
}

export interface PricingData {
  year: number;
  validFrom: string;
  validTo: string;
  price: string;
  currency: string;
  consumption_kwh?: string;
  total_amount_huf?: string;
  meter_type?: string;
  pod_id?: string;
  consumption_location?: string;
  contract_id?: string;
  fix_tozsde?: string;  // Fix/tőzsde típus (AMR, SLP stb.)
  source: string;
  imported_at?: string;
  status: 'active' | 'future' | 'expired';
}

export interface PricingStructure {
  currentPricing: PricingData;
  futurePricing: PricingData[];
  pricingHistory: PricingData[];
  lastPricingUpdate: string;
}
